import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, ArrowLeft, Lock, ChevronRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { adminContent } from '../../services/api';
import { FlowThemeSetting, MediaAsset, MediaAssetType } from 'types';
import MediaAssetPicker from '../../components/admin/MediaAssetPicker';
import ContentSyncNotice from '../../components/admin/ContentSyncNotice';
import type { AdminCourseOption, ContentSyncSummary, QuestionLibraryCatalogCourse, QuestionLibraryCourseCatalog } from '../../services/api/adminContentClient';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { LoadingAnimation } from '../../components/ui/loading-animation';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../components/ui/use-toast';
import FloatingSaveBar from '../../components/admin/FloatingSaveBar';
import CourseHome from './CourseBuilder/CourseHome';
import CourseSwitcher from './CourseBuilder/CourseSwitcher';
import CourseJumpLinks from './CourseJumpLinks';
import { useUnsavedChangesGuard } from './QuestionLibrary/useUnsavedChangesGuard';
import { FlowThemePicker } from './FlowBuilder/FlowThemePicker';
import CourseCurriculum, { DraftCourseFlow, DraftChapter, PendingChapterDraft, chapterKey, curriculumBlocks } from './CourseCurriculum';
import { CoursePicker } from 'components/admin/CoursePicker';
import type { CoursePrerequisite } from 'types';
import {
  CONTENT_TOOLS_QUERY_PARAMS,
  normalizePositiveIntegerParam,
  parsePositiveIntegerParam,
  updateContentToolsSearchParams,
} from './contentToolsQueryParams';

type CourseFlow = DraftCourseFlow;

type AdminCourse = {
  id: number;
  name: string;
  description?: string;
  has_certificate?: boolean;
  learning_outcomes?: string[];
  media?: MediaAsset | null;
  media_asset_id?: string | null;
  is_active: boolean;
  lifecycle_status?: 'published' | 'qa';
  factory_managed?: boolean;
  content_key?: string;
  content_version?: string;
  content_sync?: ContentSyncSummary | null;
  /** Theme its flows inherit unless they set their own; '' = not set. */
  theme?: FlowThemeSetting;
  flows: CourseFlow[];
  chapters?: DraftChapter[];
  prerequisites?: CoursePrerequisite[];
};

type FlowSummary = {
  id: number;
  name: string;
  title?: string;
  description?: string;
  is_active: boolean;
};

const DEFAULT_COURSE_METADATA: Partial<AdminCourse> = {
  name: '',
  description: '',
  has_certificate: false,
  learning_outcomes: [],
  media: null,
  media_asset_id: null,
  is_active: true,
  theme: '',
};

const FLOW_PICKER_PAGE_SIZE = 20;

const isBlankValue = (value: unknown) =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.every(item => typeof item === 'string' && !item.trim()));

/** Whether a new-course draft holds anything beyond the defaults (typed text, an image, a theme, a toggled option). */
const hasNewCourseInput = (metadata: Partial<AdminCourse>) => {
  const keys = new Set([...Object.keys(DEFAULT_COURSE_METADATA), ...Object.keys(metadata)]) as Set<keyof AdminCourse>;
  return Array.from(keys).some(key => {
    const value = metadata[key];
    const fallback = DEFAULT_COURSE_METADATA[key];
    if (isBlankValue(value) && isBlankValue(fallback)) return false;
    if (typeof value === 'string' && typeof fallback === 'string') return value.trim() !== fallback.trim();
    return JSON.stringify(value) !== JSON.stringify(fallback);
  });
};

export const COURSE_UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes to this course. Discard them?';

const courseSaveErrorText = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(courseSaveErrorText).filter(Boolean).join(' ');
  if (!value || typeof value !== 'object') return '';
  return Object.entries(value).map(([field, detail]) => {
    const message = courseSaveErrorText(detail);
    if (!message) return '';
    if (['detail', 'message', 'non_field_errors'].includes(field)) return message;
    const labels: Record<string, string> = { prerequisite_ids: 'Prerequisites', chapters: 'Chapters', flows: 'Modules' };
    const label = labels[field] || field.replace(/_/g, ' ').replace(/^./, letter => letter.toUpperCase());
    return `${label}: ${message}`;
  }).filter(Boolean).join(' ');
};

const CourseComposer: React.FC = () => {
  const { addToast } = useToast();
  const [searchParams, setRouterSearchParams] = useSearchParams();
  // Course Builder's own URL updates (selecting, creating, closing a course) ask for themselves through the discard
  // dialog, so the unsaved-changes navigation guard lets them through.
  const ownNavigationRef = useRef(false);
  const setSearchParams = useCallback((next: URLSearchParams, options?: { replace?: boolean }) => {
    ownNavigationRef.current = true;
    try {
      setRouterSearchParams(next, options);
    } finally {
      ownNavigationRef.current = false;
    }
  }, [setRouterSearchParams]);
  const [selected, setSelected] = useState<AdminCourse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailRevision, setDetailRevision] = useState(0);
  const [courseOptions, setCourseOptions] = useState<AdminCourseOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState('');
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const optionsRequestRef = useRef(0);
  const optionsMountedRef = useRef(true);
  const [metadata, setMetadata] = useState<Partial<AdminCourse>>({});
  const [flowItems, setFlowItems] = useState<CourseFlow[]>([]);
  const [diffPreview, setDiffPreview] = useState<string[]>([]);
  const [pendingChapter, setPendingChapter] = useState<PendingChapterDraft | null>(null);
  const [chapters, setChapters] = useState<DraftChapter[]>([]);
  const [prerequisiteIds, setPrerequisiteIds] = useState<number[]>([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [pickerChapter, setPickerChapter] = useState<DraftChapter | undefined>();
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [editorSection, setEditorSection] = useState<'details'|'curriculum'|'prerequisites'>('details');
  const [saving, setSaving] = useState<boolean>(false);
  // Lock only the submitted draft: a later draft can be edited/created while the old request settles.
  const [pendingCreateDraft, setPendingCreateDraft] = useState<number | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
  const creatingRef = useRef(isCreatingNew);
  creatingRef.current = isCreatingNew;
  const pickerRequestRef = useRef(0);
  const [isFlowPickerOpen, setIsFlowPickerOpen] = useState<boolean>(false);
  const [flowSearchTerm, setFlowSearchTerm] = useState<string>('');
  const [appliedFlowSearch, setAppliedFlowSearch] = useState<string>('');
  const [flowResults, setFlowResults] = useState<FlowSummary[]>([]);
  const [flowResultsLoading, setFlowResultsLoading] = useState<boolean>(false);
  const [flowLoadingMore, setFlowLoadingMore] = useState<boolean>(false);
  const [flowPage, setFlowPage] = useState<number>(1);
  const [flowHasMore, setFlowHasMore] = useState<boolean>(false);
  const [flowOnlyActive, setFlowOnlyActive] = useState<boolean>(true);
  const urlSelectedCourseId = useMemo(
    () =>
      parsePositiveIntegerParam(
        searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.courseBuilder.courseId),
      ),
    [searchParams],
  );
  const selectedCourseIdRef = useRef(urlSelectedCourseId);
  selectedCourseIdRef.current = urlSelectedCourseId;

  const sortByOrder = useCallback((items: CourseFlow[]) => {
    return items.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, []);

  const reindexFlowItems = useCallback((items: CourseFlow[]) => {
    return items.map((item, index) => ({ ...item, order: index }));
  }, []);

  const appendFlowItem = useCallback(
    (flowId: number, flowName?: string) => {
      if (flowItems.some(item => item.flow_id === flowId)) return false;
      const added: CourseFlow = {
        flow_id: flowId,
        flow_name: flowName ?? `Module ${flowId}`,
        order: flowItems.length,
        is_required: true,
        chapter_id: pickerChapter?.id ?? null,
        chapter_key: pickerChapter?.id ? null : pickerChapter?.client_id ?? null,
      };
      const next = [...flowItems];
      const members = pickerChapter ? next.map((item, index) => (item.chapter_key || String(item.chapter_id)) === chapterKey(pickerChapter) ? index : -1).filter(index => index >= 0) : [];
      const blocks = curriculumBlocks(flowItems, chapters);
      const chapterPosition = pickerChapter ? blocks.findIndex(block => block.chapter && chapterKey(block.chapter) === chapterKey(pickerChapter)) : -1;
      const insertAt = members.length ? members[members.length-1] + 1 : chapterPosition >= 0 ? blocks.slice(0, chapterPosition).reduce((total, block) => total + block.flows.length, 0) : next.length;
      next.splice(insertAt, 0, added);
      setFlowItems(reindexFlowItems(next));
      setDiffPreview([]);
      return true;
    },
    [reindexFlowItems, pickerChapter, flowItems, chapters],
  );

  const [catalogCourses, setCatalogCourses] = useState<QuestionLibraryCatalogCourse[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const catalogRequestRef = useRef(0);
  const catalogMountedRef = useRef(true);
  // The whole active catalog, filtered and grouped on the client. Refreshes keep the current list on screen;
  // only the first load shows the spinner.
  const loadCourses = useCallback(async () => {
    const requestId = ++catalogRequestRef.current;
    setCatalogError('');
    try {
      const response = await adminContent.questions.courseCatalog();
      if (!catalogMountedRef.current || requestId !== catalogRequestRef.current) return;
      setCatalogCourses((response.data as QuestionLibraryCourseCatalog).courses ?? []);
      setCatalogLoaded(true);
    } catch (error) {
      if (catalogMountedRef.current && requestId === catalogRequestRef.current) {
        setCatalogError('Unable to load courses.');
        setCatalogLoaded(true);
      }
    }
  }, []);
  useEffect(() => {
    catalogMountedRef.current = true;
    void loadCourses();
    return () => { catalogMountedRef.current = false; };
  }, [loadCourses]);

  const loadCourseOptions = useCallback(async () => {
    const requestId = ++optionsRequestRef.current;
    setOptionsLoading(true);
    setOptionsLoaded(false);
    setOptionsError('');
    try {
      const response = await adminContent.courses.options();
      if (!optionsMountedRef.current || requestId !== optionsRequestRef.current) return;
      setCourseOptions(response.data);
      setOptionsLoaded(true);
    } catch (error) {
      if (optionsMountedRef.current && requestId === optionsRequestRef.current) setOptionsError('Unable to load prerequisite courses. Please try again.');
    } finally {
      if (optionsMountedRef.current && requestId === optionsRequestRef.current) setOptionsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (editorSection === 'prerequisites' && selected && optionsRequestRef.current === 0) void loadCourseOptions();
  }, [editorSection, loadCourseOptions, selected]);
  useEffect(() => {
    optionsMountedRef.current = true;
    return () => { optionsMountedRef.current = false; };
  }, []);

  const applySelectedCourse = useCallback(
    (course: AdminCourse) => {
      setIsCreatingNew(false);
      setSelected(course);
      setMetadata(course);
      setChapters(course.chapters ?? []);
      setPrerequisiteIds((course.prerequisites ?? []).map(item => item.id));
      setSaveError('');
      setFlowItems(reindexFlowItems(sortByOrder(course.flows)));
      setDiffPreview([]);
      setPendingChapter(null);
    },
    [reindexFlowItems, sortByOrder],
  );

  const clearSelectedCourse = useCallback(() => {
    setSelected(null);
    setMetadata({});
    setFlowItems([]);
    setChapters([]);
    setPrerequisiteIds([]);
    setDiffPreview([]);
    setPendingChapter(null);
  }, []);

  // A started inline chapter (title or description typed) is unsaved work too.
  const pendingChapterStarted = Boolean(pendingChapter && (pendingChapter.title.trim() || pendingChapter.description.trim()));
  const contentDirty = Boolean(selected) && (
    JSON.stringify(metadata) !== JSON.stringify(selected)
    || JSON.stringify(flowItems) !== JSON.stringify(reindexFlowItems(sortByOrder(selected?.flows ?? [])))
    || JSON.stringify(chapters) !== JSON.stringify(selected?.chapters ?? [])
    || JSON.stringify(prerequisiteIds) !== JSON.stringify((selected?.prerequisites ?? []).map(item => item.id))
  );
  // A new course that hasn't been created yet is unsaved work as soon as anything is entered.
  const createDirty = isCreatingNew && hasNewCourseInput(metadata);
  const dirty = contentDirty || (Boolean(selected) && pendingChapterStarted) || createDirty;
  // A half-typed new chapter is unsaved work that the save would silently drop: ask the author to add or cancel it
  // first, and take them to it.
  const handleReviewRequest = () => {
    if (pendingChapterStarted) {
      setEditorSection('curriculum');
      addToast({
        title: 'Finish the new chapter first',
        description: 'Add the chapter you started, or cancel it, before reviewing changes.',
      });
      return;
    }
    void handlePreview();
  };
  const confirmNavigation = (action: () => void) => { if (dirty) setPendingAction(() => action); else action(); };
  const closeCourse = () => confirmNavigation(() => {
    clearSelectedCourse();
    setIsCreatingNew(false);
    setSearchParams(updateContentToolsSearchParams(searchParams, { [CONTENT_TOOLS_QUERY_PARAMS.courseBuilder.courseId]: null }), { replace: true });
  });
  // Asks before reload/close, links, navigate() and browser Back/Forward leave the Course Builder with unsaved
  // changes. Switching courses inside the builder uses the discard dialog instead.
  const allowOwnNavigation = useCallback(
    (destination: URL, current: URL) =>
      ownNavigationRef.current && destination.origin === current.origin && destination.pathname === current.pathname,
    [],
  );
  useUnsavedChangesGuard(dirty, { message: COURSE_UNSAVED_CHANGES_MESSAGE, allowNavigation: allowOwnNavigation });

  const handleSelect = useCallback(
    (courseId: number) => {
      setSearchParams(
        updateContentToolsSearchParams(searchParams, {
          [CONTENT_TOOLS_QUERY_PARAMS.courseBuilder.courseId]: courseId,
        }),
        { replace: true },
      );
    },
    [searchParams, setSearchParams],
  );

  const fetchFlowResults = useCallback(
    async ({ page: pageToLoad, append }: { page: number; append: boolean }) => {
      if (!isFlowPickerOpen) return;
      const requestId = ++pickerRequestRef.current;
      const setLoading = append ? setFlowLoadingMore : setFlowResultsLoading;
      setLoading(true);
      if (!append) {
        setFlowLoadingMore(false);
        setFlowResults([]);
      }
      try {
        const params: { search?: string; page?: number; page_size?: number; is_active?: boolean } = {
          page: pageToLoad,
          page_size: FLOW_PICKER_PAGE_SIZE,
        };
        if (appliedFlowSearch) {
          params.search = appliedFlowSearch;
        }
        if (flowOnlyActive) {
          params.is_active = true;
        }
        const response = await adminContent.flows.listSummary(params);
        if (requestId !== pickerRequestRef.current) return;
        const data = response.data;
        let results: FlowSummary[] = [];
        let totalCount: number | undefined;
        let hasNext = false;
        if (data?.results) {
          results = data.results as FlowSummary[];
          totalCount = typeof data.count === 'number' ? data.count : undefined;
          if (typeof data.next !== 'undefined') {
            hasNext = Boolean(data.next);
          }
        } else if (Array.isArray(data)) {
          results = data as FlowSummary[];
          totalCount = results.length;
        }
        setFlowResults((prev) => (append ? [...prev, ...results] : results));
        setFlowPage(pageToLoad);
        if (typeof totalCount === 'number') {
          setFlowHasMore(pageToLoad * FLOW_PICKER_PAGE_SIZE < totalCount);
        } else if (hasNext) {
          setFlowHasMore(true);
        } else {
          setFlowHasMore(results.length === FLOW_PICKER_PAGE_SIZE);
        }
      } catch (error) {
        if (requestId !== pickerRequestRef.current) return;
        console.error('Failed to load flows', error);
        addToast({ title: 'Error', description: 'Unable to load modules.', variant: 'destructive' });
        if (!append) {
          setFlowResults([]);
          setFlowHasMore(false);
        }
      } finally {
        if (requestId === pickerRequestRef.current) setLoading(false);
      }
    },
    [addToast, appliedFlowSearch, flowOnlyActive, isFlowPickerOpen],
  );

  useEffect(() => {
    if (!isFlowPickerOpen) return;
    setFlowPage(1);
    void fetchFlowResults({ page: 1, append: false });
  }, [appliedFlowSearch, fetchFlowResults, isFlowPickerOpen]);

  const handleLoadMoreFlows = useCallback(() => {
    if (!isFlowPickerOpen || !flowHasMore || flowResultsLoading || flowLoadingMore) return;
    const nextPage = flowPage + 1;
    void fetchFlowResults({ page: nextPage, append: true });
  }, [fetchFlowResults, flowHasMore, flowLoadingMore, flowPage, flowResultsLoading, isFlowPickerOpen]);

  const handleSelectFlowFromPicker = useCallback(
    (flow: FlowSummary) => {
      if (!selected || !selected.is_active) {
        addToast({
          title: 'Select a course',
          description: 'Save and select an active course before adding modules.',
          variant: 'destructive',
        });
        return;
      }
      const flowTitle = flow.title || flow.name;
      const added = appendFlowItem(flow.id, flowTitle);
      if (!added) {
        addToast({
          title: 'Duplicate module',
          description: `Module ${flowTitle} is already assigned.`,
          variant: 'destructive',
        });
        return;
      }
      addToast({
        title: 'Module added',
        description: `Module ${flowTitle} appended to the course.`,
      });
    },
    [addToast, appendFlowItem, selected],
  );

  const handleFlowPickerOpenChange = useCallback((open: boolean) => {
    setIsFlowPickerOpen(open);
  }, []);

  // Each new-course draft gets a generation. A create request only opens its course if its draft is still the one
  // on screen, so a slow response can't replace a newer draft after the author left and started another.
  const createDraftRef = useRef(0);

  const handleStartCreate = useCallback(() => {
    createDraftRef.current += 1;
    setIsCreatingNew(true);
    clearSelectedCourse();
    setMetadata({ ...DEFAULT_COURSE_METADATA });
    setSearchParams(
      updateContentToolsSearchParams(searchParams, {
        [CONTENT_TOOLS_QUERY_PARAMS.courseBuilder.courseId]: null,
      }),
      { replace: true },
    );
  }, [clearSelectedCourse, searchParams, setSearchParams]);

  const handleCancelCreate = useCallback(() => {
    createDraftRef.current += 1;
    setIsCreatingNew(false);
    clearSelectedCourse();
  }, [clearSelectedCourse]);

  useEffect(() => {
    const rawCourseId = searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.courseBuilder.courseId);

    const nextParams = updateContentToolsSearchParams(searchParams, {
      [CONTENT_TOOLS_QUERY_PARAMS.courseBuilder.courseId]:
        rawCourseId ? normalizePositiveIntegerParam(rawCourseId) : null,
    });

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let isCancelled = false;
    setDetailError('');
    if (urlSelectedCourseId === null) {
      setDetailLoading(false);
      if (!creatingRef.current) clearSelectedCourse();
      return;
    }
    setIsCreatingNew(false);
    clearSelectedCourse();
    setDetailLoading(true);
    void (async () => {
      try {
        const response = await adminContent.courses.retrieve(urlSelectedCourseId);
        if (!isCancelled) applySelectedCourse(response.data as AdminCourse);
      } catch (error) {
        if (!isCancelled) setDetailError('Unable to load this course. Please try again.');
      } finally {
        if (!isCancelled) setDetailLoading(false);
      }
    })();
    return () => { isCancelled = true; };
  }, [applySelectedCourse, clearSelectedCourse, detailRevision, urlSelectedCourseId]);

  useEffect(() => {
    if (!isFlowPickerOpen) return;
    const handler = window.setTimeout(() => {
      setAppliedFlowSearch(flowSearchTerm.trim());
    }, 300);
    return () => window.clearTimeout(handler);
  }, [flowSearchTerm, isFlowPickerOpen]);

  useEffect(() => {
    if (isFlowPickerOpen) {
      return;
    }
    pickerRequestRef.current += 1;
    setFlowSearchTerm('');
    setAppliedFlowSearch('');
    setFlowResults([]);
    setFlowPage(1);
    setFlowHasMore(false);
    setFlowResultsLoading(false);
    setFlowLoadingMore(false);
    setFlowOnlyActive(true);
  }, [isFlowPickerOpen]);

  const handlePreview = useCallback(() => {
    const changes: string[] = [];
    const previousFlows = selected?.flows ?? [];
    flowItems.forEach((flow,index) => {
      const previous = previousFlows.find(item => item.flow_id === flow.flow_id);
      if (!previous) changes.push(`Add module: ${flow.flow_name} at position ${index+1}.`);
      else {
        if (previousFlows.indexOf(previous) !== index) changes.push(`Move ${flow.flow_name} to position ${index+1}.`);
        if ((flow.chapter_key || flow.chapter_id || null) !== (previous.chapter_key || previous.chapter_id || null)) changes.push(`Place ${flow.flow_name} ${flow.chapter_id || flow.chapter_key ? `in ${chapters.find(chapter => chapterKey(chapter) === (flow.chapter_key || String(flow.chapter_id)))?.title || 'a chapter'}` : 'outside chapters'}.`);
      }
    });
    previousFlows.filter(flow => !flowItems.some(item => item.flow_id === flow.flow_id)).forEach(flow => changes.push(`Remove module from course: ${flow.flow_name}.`));
    chapters.forEach(chapter => { const previous = selected?.chapters?.find(item => chapterKey(item) === chapterKey(chapter)); if (!previous) changes.push(`Add chapter: ${chapter.title}.`); else if (JSON.stringify(previous) !== JSON.stringify(chapter)) changes.push(`Update chapter: ${chapter.title}.`); });
    selected?.chapters?.filter(chapter => !chapters.some(item => chapterKey(item) === chapterKey(chapter))).forEach(chapter => changes.push(`Remove chapter: ${chapter.title}; its remaining modules are kept.`));
    if (JSON.stringify(prerequisiteIds.slice().sort()) !== JSON.stringify((selected?.prerequisites ?? []).map(item => item.id).sort())) changes.push(`Prerequisites: ${prerequisiteIds.length ? prerequisiteIds.map(id => courseOptions.find(course => course.id === id)?.name || selected?.prerequisites?.find(course => course.id === id)?.name || `Course ${id}`).join(', ') : 'None'}. All must be completed.`);
    if (JSON.stringify(metadata) !== JSON.stringify(selected)) changes.push('Update course details.');
    setDiffPreview(changes.length ? [...changes,'Existing learner responses and progress are preserved.'] : ['No changes to save.']);
    setReviewOpen(true);
  }, [chapters, courseOptions, flowItems, metadata, prerequisiteIds, selected]);

  const handleMetaField = useCallback((field: keyof AdminCourse, value: string) => {
    setMetadata((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleLearningOutcomeChange = useCallback((index: number, value: string) => {
    setMetadata((prev) => {
      const current = Array.isArray(prev.learning_outcomes) ? [...prev.learning_outcomes] : [];
      if (index < 0 || index >= current.length) {
        return prev;
      }
      current[index] = value;
      return {
        ...prev,
        learning_outcomes: current,
      };
    });
  }, []);

  const handleAddLearningOutcome = useCallback(() => {
    setMetadata((prev) => {
      const current = Array.isArray(prev.learning_outcomes) ? [...prev.learning_outcomes] : [];
      return {
        ...prev,
        learning_outcomes: [...current, ''],
      };
    });
  }, []);

  const handleRemoveLearningOutcome = useCallback((index: number) => {
    setMetadata((prev) => {
      const current = Array.isArray(prev.learning_outcomes) ? [...prev.learning_outcomes] : [];
      if (index < 0 || index >= current.length) {
        return prev;
      }
      current.splice(index, 1);
      return {
        ...prev,
        learning_outcomes: current,
      };
    });
  }, []);

  const resizeOutcomeTextarea = useCallback((element: HTMLTextAreaElement | null) => {
    if (!element) {
      return;
    }
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  const handleMediaSelection = useCallback((asset: MediaAsset | null) => {
    setMetadata((prev) => ({ ...prev, media: asset, media_asset_id: asset?.id ?? null }));
  }, []);

  const canSetPortableIdentity = !selected?.factory_managed && !selected?.content_sync && !selected?.content_key && !selected?.content_version;

  const handleSave = useCallback(async () => {
    const trimmedName = (metadata.name ?? '').trim();
    if (!trimmedName) {
      addToast({
        title: 'Name required',
        description: 'Please provide a name before saving.',
        variant: 'destructive',
      });
      return;
    }

    if (canSetPortableIdentity && (metadata.content_key || metadata.content_version)) {
      const key = metadata.content_key ?? '';
      const version = metadata.content_version ?? '';
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(key) || key.length > 100 || !/^(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)$/.test(version) || version.length > 32) {
        const message = 'Set both a Course Key using lowercase letters, numbers and single hyphens, and a Course Version such as 1.0.0.';
        setSaveError(message);
        addToast({ title: 'Check course identity', description: message, variant: 'destructive' });
        return;
      }
    }

    setMetadata((prev) => ({ ...prev, name: trimmedName }));

    const normalizedLearningOutcomes = (metadata.learning_outcomes ?? [])
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    const basePayload: Record<string, unknown> = {
      name: trimmedName,
      description: metadata.description,
      has_certificate: typeof metadata.has_certificate === 'boolean' ? metadata.has_certificate : false,
      learning_outcomes: normalizedLearningOutcomes,
      media_asset_id: metadata.media?.id ?? metadata.media_asset_id ?? null,
      is_active: typeof metadata.is_active === 'boolean' ? metadata.is_active : true,
    };
    // Send the stored theme unchanged unless the admin picked one, so a theme this
    // build doesn't know survives unrelated edits. '' means "not set".
    if (typeof metadata.theme === 'string') {
      basePayload.theme = metadata.theme;
    }

    if (canSetPortableIdentity && (metadata.content_key || metadata.content_version)) {
      basePayload.content_key = metadata.content_key;
      basePayload.content_version = metadata.content_version;
    }

    if (isCreatingNew) {
      const draft = createDraftRef.current;
      setPendingCreateDraft(draft);
      try {
        const response = await adminContent.courses.create(basePayload);
        if (!catalogMountedRef.current) return;
        const created = (response?.data ?? {}) as AdminCourse;
        const courseId = created?.id;
        if (!courseId) {
          throw new Error('Course creation response missing id');
        }
        addToast({ title: 'Course created', description: trimmedName });
        void loadCourses();
        if (optionsRequestRef.current > 0) void loadCourseOptions();
        if (draft !== createDraftRef.current || !creatingRef.current || selectedCourseIdRef.current !== null) return;
        setIsCreatingNew(false);
        setSearchParams(
          updateContentToolsSearchParams(searchParams, {
            [CONTENT_TOOLS_QUERY_PARAMS.courseBuilder.courseId]: courseId,
          }),
          { replace: true },
        );
      } catch (error) {
        if (!catalogMountedRef.current) return;
        console.error('Failed to create course', error);
        addToast({ title: 'Create failed', description: 'Unable to create course.', variant: 'destructive' });
      } finally {
        if (catalogMountedRef.current) setPendingCreateDraft(current => current === draft ? null : current);
      }
      return;
    }

    if (!selected || !selected.is_active) return;

    setSaving(true);
    try {
      setSaveError('');
      await adminContent.courses.setCurriculum(selected.id, {
        metadata: basePayload,
        chapters: curriculumBlocks(flowItems, chapters).flatMap((block, order) => block.chapter ? [{ ...block.chapter, order }] : []),
        flows: flowItems.map((flow, order) => ({ flow_id: flow.flow_id, order, is_required: flow.is_required, chapter_id: flow.chapter_id ?? null, chapter_key: flow.chapter_key ?? null })),
        prerequisite_ids: prerequisiteIds,
      });
      if (!catalogMountedRef.current) return;
      addToast({
        title: 'Course saved',
        description: selected.content_sync
          ? 'Saved on the site. Course Factory will reconcile this revision before its next build.'
          : trimmedName,
      });
      void loadCourses();
      if (optionsRequestRef.current > 0) void loadCourseOptions();
      const refreshed = (await adminContent.courses.retrieve(selected.id)).data as AdminCourse;
      if (!catalogMountedRef.current) return;
      if (selectedCourseIdRef.current === selected.id) applySelectedCourse(refreshed);
      setReviewOpen(false);
    } catch (error) {
      if (!catalogMountedRef.current) return;
      console.error('Failed to save course', error);
      const detail = (error as { response?: { data?: unknown } }).response?.data;
      const message = courseSaveErrorText(detail) || 'Unable to save course changes. Your draft has been kept; please try again.';
      setSaveError(message);
      addToast({ title: 'Save failed', description: message, variant: 'destructive' });
    } finally {
      if (catalogMountedRef.current) setSaving(false);
    }
  }, [
    addToast,
    applySelectedCourse,
    chapters,
    prerequisiteIds,
    flowItems,
    isCreatingNew,
    loadCourses,
    loadCourseOptions,
    canSetPortableIdentity,
    metadata.content_key,
    metadata.content_version,
    metadata.description,
    metadata.has_certificate,
    metadata.is_active,
    metadata.learning_outcomes,
    metadata.media,
    metadata.media_asset_id,
    metadata.name,
    metadata.theme,
    searchParams,
    selected,
    setSearchParams,
  ]);

  const isSaving = saving || (isCreatingNew && pendingCreateDraft === createDraftRef.current);
  const isActiveSelection = selected?.is_active ?? false;
  const canManageFlows = Boolean(selected) && isActiveSelection;

  return (
    <div className="-mx-6 min-w-0 space-y-4 sm:mx-0">
      {(selected || isCreatingNew || detailLoading || detailError || urlSelectedCourseId !== null) ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="course-builder-course-nav">
          <Button size="auto" variant="ghost" className="w-auto" onClick={closeCourse}><ArrowLeft className="h-4 w-4" />All courses</Button>
          {!isCreatingNew ? (
            <CourseSwitcher
              courses={catalogCourses}
              loading={!catalogLoaded}
              error={catalogError}
              onRetry={() => void loadCourses()}
              selectedCourseId={urlSelectedCourseId}
              fallbackName={selected?.name}
              onSelect={courseId => confirmNavigation(() => handleSelect(courseId))}
            />
          ) : null}
          {!isCreatingNew && selected ? <CourseJumpLinks courseId={selected.id} current="course-builder" className="w-full sm:ml-2 sm:w-auto" /> : null}
        </div>
      ) : null}

      {detailLoading ? (
        <Card maxWidth="none" disableGlass disableAnimation><CardContent className="flex min-h-[240px] items-center justify-center"><LoadingAnimation /><span className="sr-only">Loading course details</span></CardContent></Card>
      ) : detailError ? (
        <Card maxWidth="none" disableGlass disableAnimation><CardContent><p role="alert">{detailError}</p><Button onClick={() => setDetailRevision(value => value + 1)}>Retry course details</Button></CardContent></Card>
      ) : isCreatingNew || selected ? (
        isCreatingNew || isActiveSelection ? (
          <Card maxWidth="none" disableGlass disableAnimation className="min-w-0 px-2 sm:px-6">
            {isCreatingNew ? (
              <CardHeader>
                <CardTitle className="[&_h1]:text-xl [&_h1]:leading-snug [&_h1]:break-words sm:[&_h1]:text-2xl" title="Create course" />
              </CardHeader>
            ) : (
              // The course picker above names the open course; keep the page heading for screen readers.
              <h1 className="sr-only">{`Course: ${selected?.name}`}</h1>
            )}
            <CardContent variant="custom" className={`w-full space-y-4 ${isCreatingNew ? 'pt-4' : 'pt-6'}`}>
              <fieldset disabled={isSaving} className="min-w-0 space-y-4">
              {!isCreatingNew ? <ContentSyncNotice sync={selected?.content_sync} /> : null}
              {!isCreatingNew && <div className="flex flex-wrap gap-2" role="tablist" aria-label="Course editor sections">{(['details','curriculum','prerequisites'] as const).map(section => <Button role="tab" aria-selected={editorSection === section} key={section} size="auto" variant={editorSection === section ? 'default' : 'secondary'} onClick={() => setEditorSection(section)}>{section[0].toUpperCase()+section.slice(1)}</Button>)}</div>}
              <div hidden={!isCreatingNew && editorSection !== 'details'} className="min-w-0 max-w-3xl space-y-5" data-testid="course-details-fields">
              <div className="w-full space-y-1">
                <Label htmlFor="course-name">Name</Label>
                <Input
                  id="course-name"
                  value={metadata.name ?? ''}
                  onChange={(event) => handleMetaField('name', event.target.value)}
                />
              </div>
              <div className="w-full space-y-1">
                <Label htmlFor="course-description">Description</Label>
              <Textarea
                id="course-description"
                rows={3}
                value={metadata.description ?? ''}
                onChange={(event) => handleMetaField('description', event.target.value)}
              />
            </div>
            <div className="w-full space-y-1">
              <Label htmlFor="course-learning-outcomes">Learning outcomes</Label>
              <div className="space-y-2">
                {(metadata.learning_outcomes ?? []).map((outcome, index) => (
                  <div key={`learning-outcome-${index}`} className="flex items-start gap-2">
                    <span className="w-6 shrink-0 text-sm text-muted-foreground">{index + 1}.</span>
                    <Textarea
                      value={outcome}
                      onChange={(event) => handleLearningOutcomeChange(index, event.target.value)}
                      onInput={(event) => resizeOutcomeTextarea(event.currentTarget)}
                      ref={resizeOutcomeTextarea}
                      placeholder="Describe a course learning outcome"
                      rows={2}
                      className="min-h-[2.75rem] resize-none overflow-hidden"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveLearningOutcome(index)}
                      aria-label={`Remove learning outcome ${index + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  size="auto"
                  onClick={handleAddLearningOutcome}
                  className="inline-flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add outcome
                </Button>
              </div>
            </div>
            <div className="w-full space-y-2">
              <div className="flex items-center justify-between rounded-lg border border-border/40 bg-background/40 px-3 py-2">
                <div className="pr-4">
                  <Label htmlFor="course-has-certificate" className="text-sm">
                    Has completion certificate
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Show or hide the certificate section on course completion.
                  </p>
                </div>
                <Switch
                  id="course-has-certificate"
                  checked={metadata.has_certificate ?? false}
                  onCheckedChange={(checked) =>
                    setMetadata((prev) => ({ ...prev, has_certificate: checked }))
                  }
                />
              </div>
            </div>

            <details className="group rounded-xl border border-border/60 bg-white/50" data-testid="course-advanced">
              <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold [&::-webkit-details-marker]:hidden">
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" aria-hidden="true" />
                Advanced
                <span className="min-w-0 flex-1 break-all font-normal text-muted-foreground">· Portable course identity: {selected?.content_key && selected?.content_version ? `${selected.content_key} ${selected.content_version}` : 'not set'}</span>
              </summary>
              <section className="space-y-3 border-t border-border/60 p-4" aria-labelledby="course-identity-heading">
              <h2 id="course-identity-heading" className="text-sm font-semibold">Portable course identity</h2>
              <p className="text-xs text-muted-foreground">CSV prerequisites reference this exact key and version across environments. Display names and database IDs are not portable references.</p>
              {canSetPortableIdentity ? <>
                <p className="text-xs text-muted-foreground">Optional. Set both fields once when this manual course needs to be a portable prerequisite. Once saved, its identity cannot be renamed here.</p>
                <div className="grid gap-3 sm:grid-cols-2"><div className="space-y-1"><Label htmlFor="course-content-key">Course Key</Label><Input id="course-content-key" value={metadata.content_key ?? ''} maxLength={100} placeholder="foundations" onChange={event => handleMetaField('content_key',event.target.value)} /></div><div className="space-y-1"><Label htmlFor="course-content-version">Course Version</Label><Input id="course-content-version" value={metadata.content_version ?? ''} maxLength={32} placeholder="1.0.0" onChange={event => handleMetaField('content_version',event.target.value)} /></div></div>
                <p className="text-xs text-muted-foreground">Key: lowercase letters, numbers and single hyphens. Version: MAJOR.MINOR.PATCH, for example 1.0.0.</p>
              </> : <><dl className="grid gap-3 text-sm sm:grid-cols-2"><div><dt className="text-muted-foreground">Course Key</dt><dd className="break-all font-mono">{selected?.content_key || 'Not set'}</dd></div><div><dt className="text-muted-foreground">Course Version</dt><dd className="break-all font-mono">{selected?.content_version || 'Not set'}</dd></div></dl><p className="text-xs text-muted-foreground">{selected?.factory_managed || selected?.content_sync ? 'Course Factory manages this identity. Update its governed source through Course Factory.' : 'This saved identity is read-only so existing references stay valid.'}</p></>}
              </section>
            </details>

            <MediaAssetPicker
              value={metadata.media ?? null}
              onChange={handleMediaSelection}
              mediaTypes={['image'] as MediaAssetType[]}
              label="Course image"
            description="Select an image asset to represent this course."
            disabled={isSaving}
          />

            <FlowThemePicker
              variant="course"
              value={metadata.theme ?? ''}
              lifePlanAllowed={(selected?.content_key || metadata.content_key) === 'life-plan'}
              onChange={(theme) => setMetadata((prev) => ({ ...prev, theme }))}
              disabled={isSaving}
            />

            </div>
            {canManageFlows && editorSection === 'curriculum' && <CourseCurriculum courseId={selected?.id} flows={flowItems} chapters={chapters} pendingChapter={pendingChapter} onPendingChapterChange={setPendingChapter} onChange={(flows, nextChapters) => { setFlowItems(flows); setChapters(nextChapters); }} onAddFlow={chapter => { setPickerChapter(chapter); setIsFlowPickerOpen(true); }} />}
            {canManageFlows && editorSection === 'prerequisites' && <section className="space-y-4 rounded-2xl bg-muted/30 p-4 sm:p-6" aria-labelledby="prerequisites-heading"><h2 id="prerequisites-heading" className="text-lg font-semibold">Prerequisites</h2><p className="text-sm text-muted-foreground">Learners must complete every module and pass graded quizzes in all of these courses. Prerequisites unlock courses already assigned; they do not assign additional courses.</p>
              {prerequisiteIds.map(id => { const course = courseOptions.find(item => item.id === id) || selected?.prerequisites?.find(item => item.id === id); return <div key={id} className="flex items-center gap-3 rounded-xl border border-border bg-white p-3"><Lock className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1 break-words text-sm font-medium">{course?.name || `Unavailable course ${id}`}{course?.is_active === false && <span className="block text-xs text-amber-800">Inactive prerequisite — may block learner access</span>}</span><Button variant="ghost" size="icon" aria-label={`Remove prerequisite ${course?.name || id}`} onClick={() => setPrerequisiteIds(prev => prev.filter(value => value !== id))}><Trash2 className="h-4 w-4" /></Button></div>; })}
              {optionsError && <p role="alert" className="text-sm text-destructive">{optionsError} <Button size="auto" variant="ghost" onClick={() => void loadCourseOptions()}>Retry prerequisite courses</Button></p>}
              <CoursePicker value="" loading={optionsLoading} disabled={!optionsLoaded || optionsLoading} onValueChange={value => {
                const id = Number(value);
                const visit = (current: number, path: number[]): number[] | null => {
                  if (current === selected?.id) return [...path, current];
                  if (path.includes(current)) return null;
                  for (const prerequisiteId of courseOptions.find(course => course.id === current)?.prerequisite_ids ?? []) { const cycle = visit(prerequisiteId, [...path,current]); if (cycle) return cycle; }
                  return null;
                };
                const cycle = visit(id, []);
                if (cycle) {setSaveError(`Circular prerequisite: ${[selected!.id,...cycle].map(courseId => courseOptions.find(course => course.id === courseId)?.name || courseId).join(' → ')}. Choose a different course.`); return;}
                setSaveError(''); setPrerequisiteIds(prev => [...prev,id]);
              }} options={courseOptions.filter(course => course.is_active && course.lifecycle_status !== 'qa' && course.id !== selected?.id && !prerequisiteIds.includes(course.id)).map(course => ({value:String(course.id),label:course.name}))} placeholder="Add prerequisite courses…" emptyMessage="No additional courses available" />
              {!prerequisiteIds.length && <p className="text-sm text-muted-foreground">No prerequisites. Assigned learners can start this course.</p>}
              <div role="note" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Learners who haven't completed these prerequisites will lose access until they complete them. Their progress will be preserved.</div>
            </section>}
            {isCreatingNew && <p className="rounded-xl bg-muted/50 p-4 text-sm text-muted-foreground">Create the course first, then add modules, chapters and prerequisites.</p>}
            {saveError && <p role="alert" className="rounded-xl border border-destructive/30 p-3 text-sm text-destructive">{saveError}</p>}
              {isCreatingNew ? (
                <div className="flex flex-col flex-wrap items-center gap-2">
                  <Button className="w-full sm:w-[18.75rem]" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Creating...' : 'Create Course'}
                  </Button>
                  <Button variant="ghost" onClick={() => confirmNavigation(handleCancelCreate)} disabled={isSaving} className="w-full sm:w-[18.75rem]">
                    Cancel
                  </Button>
                </div>
              ) : canManageFlows ? (
                <FloatingSaveBar
                  dirty={dirty}
                  // Nothing to review while the only change is a chapter card that hasn't been added yet.
                  canSave={contentDirty}
                  saving={isSaving}
                  onDiscard={() => confirmNavigation(() => { if (selected) applySelectedCourse(selected); })}
                  onSave={handleReviewRequest}
                  saveLabel="Review & save"
                  testId="course-builder-save-bar"
                />
              ) : null}
              </fieldset>
            </CardContent>
          </Card>
        ) : (
          <Card maxWidth="none" disableGlass disableAnimation>
            <CardContent className="flex min-h-[240px] items-center justify-center text-sm text-muted-foreground">
              This course is inactive and cannot be edited. Activate it to make changes.
            </CardContent>
          </Card>
        )
      ) : urlSelectedCourseId !== null ? (
        <Card maxWidth="none" disableGlass disableAnimation><CardContent className="flex min-h-[240px] items-center justify-center"><LoadingAnimation /><span className="sr-only">Loading course details</span></CardContent></Card>
      ) : (
        <CourseHome
          courses={catalogCourses}
          loading={!catalogLoaded}
          error={catalogError}
          onRetry={() => void loadCourses()}
          onSelect={courseId => confirmNavigation(() => handleSelect(courseId))}
          onCreate={() => confirmNavigation(handleStartCreate)}
          createDisabled={isCreatingNew}
        />
      )}

      <Dialog open={Boolean(pendingAction)} onOpenChange={open => { if (!open) setPendingAction(null); }}><DialogContent><DialogHeader><DialogTitle>Discard unsaved changes?</DialogTitle><DialogDescription>Your course edits have not been saved.</DialogDescription></DialogHeader><div className="flex justify-end gap-2"><Button size="auto" variant="ghost" onClick={() => { pendingAction?.(); setPendingAction(null); }}>Discard changes</Button><Button size="auto" onClick={() => setPendingAction(null)}>Keep editing</Button></div></DialogContent></Dialog>
      <Dialog open={reviewOpen} onOpenChange={open => { if (!saving) setReviewOpen(open); }}>
        <DialogContent closeDisabled={saving}>
          <DialogHeader>
            <DialogTitle>Review changes to {metadata.name}</DialogTitle>
            <DialogDescription>Review the curriculum and access requirements before saving.</DialogDescription>
          </DialogHeader>
          <ul className="list-disc space-y-2 pl-5 text-sm">{diffPreview.map((item,index) => <li key={index}>{item}</li>)}</ul>
          {JSON.stringify(prerequisiteIds.slice().sort()) !== JSON.stringify((selected?.prerequisites ?? []).map(item => item.id).sort()) && (
            <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Learners who haven't completed these prerequisites will lose access until they complete them. Their progress will be preserved.</p>
          )}
          {saveError && <p role="alert" className="text-sm text-destructive">{saveError}</p>}
          <div className="flex justify-end gap-2">
            <Button size="auto" variant="ghost" disabled={saving} onClick={() => setReviewOpen(false)}>Keep editing</Button>
            <Button size="auto" disabled={saving} onClick={handleSave}>{saving ? 'Saving...' : 'Confirm and save'}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={isFlowPickerOpen} onOpenChange={handleFlowPickerOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{pickerChapter ? `Add modules to ${pickerChapter.title}` : 'Select a module'}</DialogTitle>
            <DialogDescription>Search by module name to attach it to this course.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Input
                value={flowSearchTerm}
                onChange={(event) => setFlowSearchTerm(event.target.value)}
                placeholder="Search modules by name"
                autoFocus
                className="sm:max-w-sm"
              />
              <div className="flex items-center gap-2">
                <Switch
                  id="flow-only-active"
                  checked={flowOnlyActive}
                  onCheckedChange={setFlowOnlyActive}
                />
                <Label
                  htmlFor="flow-only-active"
                  className="text-sm text-muted-foreground"
                >
                  Only show active modules
                </Label>
              </div>
            </div>
            {flowResultsLoading && flowResults.length === 0 ? (
              <div className="flex h-40 items-center justify-center">
                <LoadingAnimation size="sm" />
              </div>
            ) : flowResults.length > 0 ? (
              <div className="max-h-[340px] overflow-y-auto rounded-lg border border-border/60">
                <div className="divide-y divide-border/40">
                  {flowResults.map((flow) => {
                    const alreadyAdded = flowItems.some((item) => item.flow_id === flow.id);
                    return (
                      <div key={flow.id} className="flex items-start gap-3 p-4">
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="truncate font-medium text-foreground">{flow.title || flow.name}</div>
                          {flow.description ? (
                            <p className="text-xs text-muted-foreground">{flow.description}</p>
                          ) : null}
                          {!flow.is_active ? (
                            <p className="text-xs font-medium text-amber-300">Inactive module</p>
                          ) : null}
                        </div>
                        <Button
                          type="button"
                          size="auto"
                          variant={alreadyAdded ? 'secondary' : 'default'}
                          disabled={alreadyAdded}
                          onClick={() => handleSelectFlowFromPicker(flow)}
                        >
                          {alreadyAdded ? 'Added' : 'Add'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border/50 p-6 text-center text-sm text-muted-foreground">
                No modules found. Try a different search.
              </div>
            )}
            {flowHasMore ? (
              <div className="flex justify-center">
                <Button
                  type="button"
                  variant="secondary"
                  size="auto"
                  onClick={handleLoadMoreFlows}
                  disabled={flowLoadingMore}
                >
                  {flowLoadingMore ? 'Loading...' : 'Load more'}
                </Button>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseComposer;
